import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"
import {
  appendOrganizationClientIds,
  organizationClientIdsKey,
} from "../../lib/utils/organizationReportClientIds"

export const ORGANIZATION_CONVERSATION_TOPIC_DETAILS_PAGE_SIZE = 25

export function conversationTopicDetailsKey({
  organizationId,
  topic,
  startDate,
  endDate,
  page = 0,
  size = ORGANIZATION_CONVERSATION_TOPIC_DETAILS_PAGE_SIZE,
  clientIds,
} = {}) {
  return `${String(organizationId ?? "")}|${String(topic ?? "")}|${String(startDate ?? "")}|${String(endDate ?? "")}|${String(page)}|${String(size)}|${organizationClientIdsKey(clientIds)}`
}

/**
 * GET /api/v1/report/organizations/{organizationId}/messages/conversation-topics/details
 *   ?topic=&startDate=&endDate=&page=&size=&client_ids=
 */
export const fetchOrganizationConversationTopicDetails = createAsyncThunk(
  "organizationConversationTopicDetails/fetch",
  async (
    {
      organizationId,
      topic,
      startDate,
      endDate,
      page = 0,
      size = ORGANIZATION_CONVERSATION_TOPIC_DETAILS_PAGE_SIZE,
      clientIds,
    } = {},
    { rejectWithValue },
  ) => {
    try {
      if (organizationId == null || organizationId === "") {
        return rejectWithValue("organizationId is required")
      }
      if (!String(topic ?? "").trim()) {
        return rejectWithValue("topic is required")
      }
      if (!startDate || !endDate) {
        return rejectWithValue("startDate and endDate are required")
      }

      const query = new URLSearchParams({
        topic: String(topic).trim(),
        startDate,
        endDate,
        page: String(page),
        size: String(size),
      })
      appendOrganizationClientIds(query, clientIds)
      const data = await get(
        `report/organizations/${organizationId}/messages/conversation-topics/details?${query}`,
      )

      return {
        data,
        organizationId,
        topic: String(topic).trim(),
        startDate,
        endDate,
        page,
        size,
        fetchKey: conversationTopicDetailsKey({
          organizationId,
          topic,
          startDate,
          endDate,
          page,
          size,
          clientIds,
        }),
      }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch conversation topic details")
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg?.force) return true
      const key = conversationTopicDetailsKey(arg)
      const state = getState().organizationConversationTopicDetails
      if (state?.status === "loading" && state?.requestedFetchKey === key) return false
      if (state?.status === "succeeded" && state?.fetchedFetchKey === key) return false
      return true
    },
  },
)

const initialState = {
  data: null,
  status: "idle",
  error: null,
  organizationId: null,
  topic: null,
  startDate: null,
  endDate: null,
  requestedFetchKey: null,
  fetchedFetchKey: null,
}

const organizationConversationTopicDetailsSlice = createSlice({
  name: "organizationConversationTopicDetails",
  initialState,
  reducers: {
    clearOrganizationConversationTopicDetails: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganizationConversationTopicDetails.pending, (state, action) => {
        const arg = action.meta.arg || {}
        state.status = "loading"
        state.error = null
        state.topic = String(arg.topic ?? "").trim() || null
        state.requestedFetchKey = conversationTopicDetailsKey(arg)
      })
      .addCase(fetchOrganizationConversationTopicDetails.fulfilled, (state, action) => {
        if (action.payload?.fetchKey !== state.requestedFetchKey) return
        state.status = "succeeded"
        state.data = action.payload?.data ?? null
        state.organizationId = action.payload?.organizationId ?? null
        state.topic = action.payload?.topic ?? null
        state.startDate = action.payload?.startDate ?? null
        state.endDate = action.payload?.endDate ?? null
        state.fetchedFetchKey = action.payload?.fetchKey ?? null
      })
      .addCase(fetchOrganizationConversationTopicDetails.rejected, (state, action) => {
        if (conversationTopicDetailsKey(action.meta.arg || {}) !== String(state.requestedFetchKey ?? "")) {
          return
        }
        state.status = "failed"
        state.error = action.payload ?? "Failed to fetch conversation topic details"
        state.data = null
        state.fetchedFetchKey = state.requestedFetchKey
      })
  },
})

export const { clearOrganizationConversationTopicDetails } =
  organizationConversationTopicDetailsSlice.actions
export default organizationConversationTopicDetailsSlice.reducer
